import { CommonModule } from "@angular/common";
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    Output,
    effect,
    inject,
    signal
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ContactService } from "@app/shared/services/contact.service";
import { ReportCardBenchmarkComponent } from "@app/views/auth/report/components/report-card-benchmark/report-card-benchmark.component";
import { EventsService } from "@arch/domain/application/services/events.service";
import { CompanyEntity, ReportEntity } from "@arch/domain/entity";
import { BenchmarkingEntity } from "@arch/domain/entity/benchmarking.entity";
import {
    GetCompanyBenchmarkingController,
    GetSeriesBenchmarkingController
} from "@arch/presentation/controllers";
import { environment } from "@environments/environment";
import { DeviceService } from "@shared/services/device.service";
import { CarouselModule, OwlOptions } from "ngx-owl-carousel-o";
import { BenchmarkDataSourceInfoDialogComponent } from "../../preview/benchmark-data-source-info/benchmark-data-source-info-dialog.component";

@Component({
    selector: "app-report-beachmarking-section",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        ReportCardBenchmarkComponent,
        CarouselModule,
        CommonModule
    ],
    templateUrl: "./report-beachmarking-section.component.html",
    styleUrl: "./report-beachmarking-section.component.scss"
})
export class ReportBeachmarkingSectionComponent implements AfterViewInit {
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) previewMode: boolean;
    @Input({ required: false }) pdfMode: boolean = false;
    @Input({ required: false }) locked: boolean = false;

    @Input() set loading(value: boolean) {
        this.loadingSignal.set(value);
    }
    get loading(): boolean {
        return this.loadingSignal();
    }
    @Output() handleBenchmarkingDataLoaded = new EventEmitter<boolean>();
    public loadingSignal = signal(false);

    private readonly getCompanyBenchmarkingController = inject(
        GetCompanyBenchmarkingController
    );
    private readonly getSeriesBenchmarkingController = inject(
        GetSeriesBenchmarkingController
    );

    private readonly eventsService = inject(EventsService);
    private readonly deviceService = inject(DeviceService);
    private readonly route = inject(ActivatedRoute);
    public readonly isMobile = this.deviceService.getIsMobile();
    private readonly contactService = inject(ContactService);
    public contact = this.contactService.get();

    private readonly dialog = inject(MatDialog);

    public companyBenchmarking: CompanyEntity;
    public seriesBenchmark: BenchmarkingEntity[] = [];
    public loadingBenchmarking = false;
    public customOptions: OwlOptions;
    public showTooltip = false;

    constructor() {
        effect(() => {
            if (this.loading) return;
            this.setCompanyBenchmarking();
            this.setSeriesBenchmark();
        });
    }

    ngAfterViewInit(): void {
        this.customOptions = this.getCustomOptions();
    }

    private getCustomOptions(): OwlOptions {
        if (this.pdfMode) {
            return {
                loop: false,
                rewind: false,
                mouseDrag: false,
                touchDrag: false,
                pullDrag: false,
                dots: false,
                navSpeed: 0,
                items: 3.1,
                autoWidth: false,
                nav: false,
                margin: 10,
                autoplay: false
            };
        } else {
            const options = {
                loop: false,
                rewind: true,
                mouseDrag: true,
                touchDrag: true,
                pullDrag: true,
                dots: this.isMobile() || this.previewMode ? true : false,
                navSpeed: 700,
                items: this.previewMode && !this.isMobile() ? 2.4 : 1.07,
                responsive: {
                    0: {
                        items:
                            this.previewMode && !this.isMobile() ? 2.4 : 1.07,
                        dots: true
                    },
                    900: {
                        items: 2.8,
                        dots: true
                    },
                    1000: {
                        items: 3.3,
                        dots: false
                    }
                },
                autoWidth: true,
                nav: false,
                margin: 10,
                autoplay: this.isMobile() ? true : false
            };
            return options;
        }
    }

    private async setCompanyBenchmarking(): Promise<void> {
        const { companyId } = this.route.snapshot.queryParams;
        if (!companyId) return;
        this.companyBenchmarking =
            await this.getCompanyBenchmarkingController.execute({
                companyId
            });
    }

    public async setSeriesBenchmark(): Promise<void> {
        this.loadingBenchmarking = true;
        this.seriesBenchmark =
            await this.getSeriesBenchmarkingController.execute({
                company: this.company,
                sources: this.report.options.sources,
                options: {
                    date: this.report.options.endDate,
                    period: this.report.options.period,
                    scenarioId: this.report.options.scenario.props._id
                }
            });
        this.loadingBenchmarking = false;
        this.handleBenchmarkingDataLoaded.emit(this.seriesBenchmark.length > 0);
    }

    public onOpenBenchmarkingDialog(): void {
        this.onRedirectToBenchmarking();
    }

    public onBenchmarkDataSource(): void {
        this.onOpenHelpCenterBenchmarking();
        this.dialog.open(BenchmarkDataSourceInfoDialogComponent, {
            width: "300px"
        });
    }

    public onOpenHelpCenterBenchmarking(): void {
        window.open(
            "https://ajuda.nucont.com/centraldeconhecimento/o-que-é-o-benchmarking-da-nucont-e-por-que-ele-é-tão-poderoso",
            "_blank"
        );
    }

    public onRedirectToBenchmarking(): void {
        this.eventsService.send("dash.benchmarking.redirect");
        let url = `${environment.BENCHMARKING_URL}/dashboard?companyId=${this.company.props._id}`;
        if (this.contact.props.role === "Super")
            url += `&liveToken=${environment.LIVE_TOKEN}&userId=${this.contact?.props?._id}`;
        else if (this.contact.props._id != "convidadoId") {
            if (this.contact.props.type === "user")
                url += `&liveToken=${environment.LIVE_TOKEN}&userId=${this.contact?.props?._id}`;
            else
                url += `&liveToken=${environment.LIVE_TOKEN}&contactId=${this.contact?.props?._id}`;
        } else url += `&liveToken=${environment.LIVE_TOKEN}`;
        window.open(url, "_blank");
    }
}
